import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import React, { useMemo, useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Icon } from "@iconify/react";
import { useSelector, shallowEqual } from "react-redux";
import { useDebounce } from "../../../../../Hooks/useDebounce";

const FilterSections = React.memo(({ formik }) => {
  const { values, handleChange, handleSubmit, setFieldValue } = formik;
  const { fromDate, toDate, search, archiveViewChecked, tag } = values;

  // Select only what is needed and prevent re-renders with shallowEqual
  const { dataUserUnder } = useSelector((state) => state.general, shallowEqual);
  const { tagNames } = useSelector((state) => state.appraisal, shallowEqual);
  // -----------------------------------------------------------------------------
  const [localSearch, setLocalSearch] = useState(search);
  const [localTag, setLocalTag] = useState(tag);
  const [localFromDate, setLocalFromDate] = useState(fromDate);
  const [localToDate, setLocalToDate] = useState(toDate);

  // -----------------------------------------------------------------------
  const debouncedSearch = useDebounce(localSearch, 400);
  const debouncedTag = useDebounce(localTag, 300);
  const debouncedFromDate = useDebounce(localFromDate, 300);
  const debouncedToDate = useDebounce(localToDate, 300);
  // --------------------------------------------------------------------------------

  useEffect(() => {
    setFieldValue("tags", debouncedTag);
  }, [debouncedTag, setFieldValue]);
  useEffect(() => {
    setFieldValue("search", debouncedSearch);
  }, [debouncedSearch, setFieldValue]);
  useEffect(() => {
    setFieldValue("fromDate", debouncedFromDate);
  }, [debouncedFromDate, setFieldValue]);

  useEffect(() => {
    setFieldValue("toDate", debouncedToDate);
  }, [debouncedToDate, setFieldValue]);

  // ----------------------------------------------------------------------------
  // Memoized options for tag dropdown
  const tagOptions = useMemo(
    () =>
      tagNames?.map((tag) => (
        <MenuItem key={tag.appraisal_tag_id} value={tag.appraisal_tag_id}>
          {tag.appraisal_tag_name}
        </MenuItem>
      )),
    [tagNames]
  );

  const handleChanges = useCallback(
    (event, newValue) => {
      setFieldValue("user", newValue);
    },
    [setFieldValue]
  );

  const handleDateChange = useCallback(
    (field) => (newValue) => {
      setFieldValue(field, newValue.format("YYYY-MM-DD"));
    },
    [setFieldValue]
  );

  const handleArchiveSwitch = useCallback(
    (e) => {
      setFieldValue("archiveViewChecked", e.target.checked);
    },
    [setFieldValue]
  );

  return (
    <Box>
      <Box pt={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="demo-simple-select-label">Quarters</InputLabel>
          <Select
            size="small"
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={localTag || ""}
            name="tag"
            label="Quarters"
            onChange={(e) => setLocalTag(e.target.value)}
            sx={{
              borderRadius: 2,
              backgroundColor: "#fff",
            }}
          >
            {tagOptions}
          </Select>
        </FormControl>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "center", pt: 2 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            name="fromDate"
            label="From Date"
            value={localFromDate ? dayjs(localFromDate) : null}
            onChange={(newValue) =>
              setLocalFromDate(newValue?.format("YYYY-MM-DD"))
            }
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                sx: {
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                },
              },
            }}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            name="toDate"
            label="To Date"
            value={localToDate ? dayjs(localToDate) : null}
            onChange={(newValue) =>
              setLocalToDate(newValue?.format("YYYY-MM-DD"))
            }
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                sx: {
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Stack>

      <TextField
        size="small"
        label="Search"
        name="search"
        value={localSearch}
        onChange={(e) => setLocalSearch(e.target.value)}
        fullWidth
        sx={{
          mt: 1,
          "& .MuiOutlinedInput-root": {
            borderRadius: "15px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Icon icon="ic:sharp-search" width="24" height="24" />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={2} mt={1}>
        <Box
          sx={{
            borderRadius: 4,
            backgroundColor: "#f0f0f0",
            p: 0.4,
            pl: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={archiveViewChecked}
                onChange={handleArchiveSwitch}
                name="archiveViewChecked"
              />
            }
            label="Archived"
          />
        </Box>

        <Box sx={{ width: "100%" }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            sx={{
              width: "100%",
              borderRadius: 4,
              backgroundColor: "#103996",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#103996",
                boxShadow: "none",
              },
            }}
          >
            <Icon icon="ic:round-filter-list" width="24" height="24" />
            Filter Goals
          </Button>
        </Box>
      </Stack>
    </Box>
  );
});

export default FilterSections;
